import { useMemo, useState } from "react";
import AppShell from "@/components/AppShell";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Trash2, Sparkles } from "lucide-react";
import {
  fileToDataUrl,
  getHomepageContent,
  getSessionUser,
  saveHomepageContent,
} from "@/lib/eisStore";

function ImageGrid({ title, items, onUpload, onRemove }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle style={{ color: "#0b1f7a" }}>{title}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          {items.map((item) => (
            <div key={item.id} className="rounded-xl overflow-hidden border border-slate-200">
              <div className="relative">
                <img src={item.src} alt={title} className="h-36 w-full object-cover" />
                <Button
                  type="button"
                  size="icon"
                  variant="destructive"
                  className="absolute right-2 top-2 h-8 w-8 rounded-full shadow-md"
                  onClick={() => onRemove(item.id)}
                  aria-label={`Remove ${title} image`}
                >
                  <Trash2 size={14} />
                </Button>
              </div>
            </div>
          ))}
        </div>
        <div>
          <Label>Replace/Add image</Label>
          <Input
            type="file"
            accept="image/*"
            className="mt-1"
            onChange={onUpload}
          />
        </div>
      </CardContent>
    </Card>
  );
}

export default function EditHomepage() {
  const user = getSessionUser();
  const homepage = useMemo(() => getHomepageContent(), []);
  const [aboutUs, setAboutUs] = useState(homepage.aboutUs || "");
  const [events, setEvents] = useState(homepage.events || []);
  const [achievements, setAchievements] = useState(homepage.achievements || []);

  const uploadEvent = async (file) => {
    if (!file) return;
    const src = await fileToDataUrl(file);
    setEvents((current) => [
      { id: crypto.randomUUID(), src },
      ...current,
    ]);
  };

  const uploadAchievement = async (file) => {
    if (!file) return;
    const src = await fileToDataUrl(file);
    setAchievements((current) => [
      { id: crypto.randomUUID(), src },
      ...current,
    ]);
  };

  const removeEvent = (id) => {
    setEvents((current) => current.filter((item) => item.id !== id));
  };

  const removeAchievement = (id) => {
    setAchievements((current) => current.filter((item) => item.id !== id));
  };

  const save = () => {
    saveHomepageContent({
      aboutUs,
      events,
      achievements,
    });
    toast.success("Homepage updated");
  };

  return (
    <AppShell>
      <div data-testid="edit-homepage-page" className="space-y-6">
        <div>
          <h2 className="text-2xl md:text-3xl font-bold" style={{ color: "#0b1f7a" }}>
            Edit Homepage
          </h2>
          <p className="text-slate-500 mt-1 text-sm">
            Update the about us copy and homepage pictures.
          </p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle style={{ color: "#0b1f7a" }}>About Us</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <Textarea
              value={aboutUs}
              onChange={(e) => setAboutUs(e.target.value)}
              className="min-h-56"
            />
          </CardContent>
        </Card>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <ImageGrid
            title="Events Photos"
            items={events}
            onUpload={(e) => uploadEvent(e.target.files?.[0])}
            onRemove={removeEvent}
          />
          <ImageGrid
            title="Achievements Photos"
            items={achievements}
            onUpload={(e) => uploadAchievement(e.target.files?.[0])}
            onRemove={removeAchievement}
          />
        </div>

        <div className="flex justify-end">
          <Button
            type="button"
            onClick={save}
            className="rounded-full text-white"
            style={{ backgroundColor: "#0b1f7a" }}
          >
            <Sparkles size={16} className="mr-2" />
            Save Homepage
          </Button>
        </div>
      </div>
    </AppShell>
  );
}
